import { useState } from 'react'
import { X, Download, FileText, Code, Users } from 'lucide-react'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

interface SystemDocsProps {
  isOpen: boolean
  onClose: () => void
}

export function SystemDocs({ isOpen, onClose }: SystemDocsProps) {
  const [activeTab, setActiveTab] = useState<'technical' | 'non-technical'>('non-technical')
  const [isExporting, setIsExporting] = useState(false)

  if (!isOpen) return null

  const handleExportPDF = async () => {
    setIsExporting(true)
    try {
      const createPdfContext = () => {
        const pdf = new jsPDF('p', 'mm', 'a4')
        pdf.setLineHeightFactor(1.2)
        const pageWidth = pdf.internal.pageSize.getWidth()
        const pageHeight = pdf.internal.pageSize.getHeight()
        const margin = 15
        const contentWidth = pageWidth - margin * 2
        let yPosition = margin

        const addText = (
          text: string,
          fontSize: number,
          style: 'normal' | 'bold' = 'normal',
          color: [number, number, number] = [0, 0, 0]
        ) => {
          pdf.setFontSize(fontSize)
          pdf.setFont('helvetica', style)
          pdf.setCharSpace(0)
          pdf.setTextColor(color[0], color[1], color[2])

          const lines = pdf.splitTextToSize(text, contentWidth)
          const lineHeight = fontSize * 0.5

          for (const line of lines) {
            if (yPosition + lineHeight > pageHeight - margin) {
              pdf.addPage()
              yPosition = margin
            }
            pdf.text(line, margin, yPosition)
            yPosition += lineHeight
          }
        }

        const addSpacing = (space: number) => {
          yPosition += space
        }

        const addHeading = (text: string, level: number) => {
          if (level === 1) {
            addSpacing(10)
            addText(text, 18, 'bold', [51, 65, 85])
            addSpacing(6)
          } else if (level === 2) {
            addSpacing(8)
            addText(text, 14, 'bold', [71, 85, 105])
            addSpacing(4)
          } else {
            addSpacing(5)
            addText(text, 12, 'bold', [100, 116, 139])
            addSpacing(3)
          }
        }

        const addParagraph = (text: string) => {
          addText(text, 10, 'normal', [30, 41, 59])
          addSpacing(4)
        }

        const addBullet = (text: string, indent: number = 0) => {
          pdf.setFontSize(10)
          pdf.setFont('helvetica', 'normal')
          pdf.setCharSpace(0)
          pdf.setTextColor(30, 41, 59)

          const bulletWidth = 5
          const indentWidth = indent * 3
          const textWidth = contentWidth - bulletWidth - indentWidth
          const lines = pdf.splitTextToSize(text, textWidth)
          const lineHeight = 5

          for (let i = 0; i < lines.length; i++) {
            if (yPosition + lineHeight > pageHeight - margin) {
              pdf.addPage()
              yPosition = margin
            }
            if (i === 0) {
              pdf.text('•', margin + indentWidth, yPosition)
            }
            pdf.text(lines[i], margin + indentWidth + bulletWidth, yPosition)
            yPosition += lineHeight
          }
        }

        const addCodeBlock = (text: string) => {
          const fontSize = 9
          pdf.setFontSize(fontSize)
          pdf.setFont('courier', 'normal')
          pdf.setCharSpace(0)
          pdf.setTextColor(30, 41, 59)

          const padding = 3
          const maxWidth = contentWidth - padding * 2
          const lines = text.replace(/\t/g, '  ').split('\n')
          const wrapped = lines.flatMap((line) =>
            pdf.splitTextToSize(line === '' ? ' ' : line, maxWidth)
          )
          const lineHeight = fontSize * 0.55
          const blockHeight = wrapped.length * lineHeight + padding * 2

          if (yPosition + blockHeight > pageHeight - margin) {
            pdf.addPage()
            yPosition = margin
          }

          pdf.setFillColor(248, 250, 252)
          pdf.setDrawColor(203, 213, 225)
          pdf.roundedRect(margin, yPosition, contentWidth, blockHeight, 2, 2, 'FD')

          let textY = yPosition + padding + lineHeight
          for (const line of wrapped) {
            pdf.text(line, margin + padding, textY)
            textY += lineHeight
          }

          yPosition += blockHeight + 2
        }

        return {
          pdf,
          addHeading,
          addParagraph,
          addBullet,
          addCodeBlock,
          addSpacing,
        }
      }

      const exportSection = async (sectionId: string, filename: string) => {
        const element = document.getElementById(sectionId)
        if (!element) throw new Error(`Missing section: ${sectionId}`)

        // Clone the section to avoid layout issues and force a fixed width/padding for PDF capture
        const clone = element.cloneNode(true) as HTMLElement
        clone.classList.remove('hidden')
        clone.style.position = 'absolute'
        clone.style.left = '-9999px'
        clone.style.top = '0'
        clone.style.width = '800px'
        clone.style.padding = '24px'
        clone.style.background = '#ffffff'
        clone.style.boxSizing = 'border-box'
        clone.style.overflowWrap = 'break-word'
        clone.style.wordBreak = 'break-word'
        clone.style.lineHeight = '1.4'

        document.body.appendChild(clone)

        const canvas = await html2canvas(clone, {
          scale: 1.3,
          useCORS: true,
          logging: false,
          scrollX: 0,
          scrollY: -window.scrollY,
          backgroundColor: '#ffffff',
        })

        document.body.removeChild(clone)

        const imgData = canvas.toDataURL('image/png')
        const pdf = new jsPDF('p', 'mm', 'a4')
        const pageWidth = pdf.internal.pageSize.getWidth()
        const pageHeight = pdf.internal.pageSize.getHeight()
        const margin = 10
        const imgWidth = pageWidth - margin * 2
        const imgHeight = (canvas.height * imgWidth) / canvas.width

        let heightLeft = imgHeight
        let position = margin

        pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight)
        heightLeft -= pageHeight

        while (heightLeft > 0) {
          position = heightLeft - imgHeight + margin
          pdf.addPage()
          pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight)
          heightLeft -= pageHeight
        }

        pdf.save(filename)
      }

      // Management: capture rendered HTML (keeps visuals)
      await exportSection('management-doc', 'Rups_Groups_Documentation_Management.pdf')

      // Technical: generate text-based PDF for clean code formatting and small file size
      const { pdf, addHeading, addParagraph, addBullet, addCodeBlock } = createPdfContext()

      addHeading('Rups Groups - Technical Documentation', 1)
      addHeading('NZ Railways Staff Welfare Trust', 2)
      addParagraph(`Generated: ${new Date().toLocaleDateString('en-NZ', { year: 'numeric', month: 'long', day: 'numeric' })}`)

      addHeading('System Architecture', 2)
      addCodeBlock(`[User Browser] <--> [Vercel Edge Network (React App + API Routes)]
                          |
                          v
              [Supabase PostgreSQL DB + Auth + Storage]
                          ^
                          |
    [GitHub] --> [GitHub Actions] --> [Deploy to Vercel]`)

      addHeading('Technology Stack', 2)
      addParagraph('Frontend:')
      addBullet('React 19.1.1 - UI framework with hooks')
      addBullet('TypeScript 5.x - Type safety')
      addBullet('Vite 7.1.7 - Build tool and dev server')
      addBullet('TanStack Query v5 - Data fetching and caching')
      addBullet('React Router - Client-side routing')
      addBullet('Tailwind CSS - Utility-first styling')
      addBullet('Lucide Icons - Icon library')

      addParagraph('Backend/Services:')
      addBullet('Supabase - Backend-as-a-Service (PostgreSQL + Auth + Storage)')
      addBullet('Row Level Security (RLS) - Database-level authorization')

      addParagraph('Hosting & Deployment:')
      addBullet('Vercel - Static React hosting with global edge delivery')
      addBullet('Vercel Serverless Functions - API routes in /api')
      addBullet('GitHub - Version control')
      addBullet('GitHub Actions - CI/CD pipeline')

      addHeading('Project Structure', 2)
      addCodeBlock(`nzrail-react/
├── src/
│   ├── app/              # Application-level code (router)
│   ├── features/         # Feature modules
│   │   ├── auth/         # Authentication (login, portal)
│   │   └── medical/      # Medical insurance features
│   │       ├── accounts/
│   │       ├── calculator/
│   │       ├── claims/
│   │       ├── memberlist/
│   │       ├── monthlyReports/
│   │       ├── previousPayments/
│   │       └── rates/
│   ├── shared/           # Shared code
│   │   ├── components/
│   │   ├── hooks/
│   │   └── lib/
│   └── styles/           # Global styles
├── dist/                 # Build output
└── public/               # Static assets`)

      addHeading('Environment Variables', 2)
      addParagraph('Required environment variables (.env file):')
      addCodeBlock(`VITE_SUPABASE_URL=https://fzxgdawafatrfpgdftrg.supabase.co
VITE_SUPABASE_ANON_KEY=<your-supabase-anon-key>`)
      addParagraph('Note: The Supabase URL is safe to expose. The anon key is also public but is restricted by RLS policies.')

      addHeading('Vercel Deployment', 2)
      addBullet('Hosting: Vercel serves the React build and Node API routes from /api')
      addBullet('Build configuration: vercel.json (builds nzrail-react and outputs to dist/)')
      addBullet('CI/CD: .github/workflows/deploy-app.yml uses Vercel CLI with VERCEL_TOKEN secret')
      addBullet('Routing: vercel.json rewrites all routes to index.html for SPA routing')

      addHeading('Database Schema', 2)
      addParagraph('Key Tables:')
      addBullet('medical_memberlist - Core member data (UUID primary key)')
      addBullet('medical_claims - Claims tracking')
      addBullet('medical_payments - Payment history')
      addBullet('medical_rate_cards - Premium rate cards (integer ID)')
      addBullet('medical_monthly_rates - Rate table joined to cards')
      addBullet('Lookup tables: All use UUID except rate cards (integer)')
      addParagraph('Row Level Security (RLS): All tables have RLS policies requiring authentication; users only access authorized data.')

      addHeading('Development Workflow', 2)
      addParagraph('Local Development:')
      addCodeBlock(`npm install              # Install dependencies
npm run dev               # Start dev server (http://localhost:5173)
npm run build             # Build for production
npm run preview           # Preview production build`)

      addParagraph('Git Workflow:')
      addCodeBlock(`git checkout -b feature/my-feature    # Create feature branch
git add .                              # Stage changes
git commit -m "message"                # Commit changes
git push origin feature/my-feature     # Push to GitHub
# Merge to main when ready`)

      addParagraph('Deployment (CI/CD):')
      addCodeBlock(`npm run build                             # Build the React app
vercel pull --yes --environment=production    # Sync env vars (CI)
vercel deploy --prebuilt --prod               # Publish to Vercel edge network
# Site live in ~2-5 minutes`)

      addHeading('Key Features Implementation', 2)
      addParagraph('Calculator Pricing Logic:')
      addParagraph('Uses age-banded monthly rates from medical_monthly_rates. Formula: Base Premium + Dental/Optical + Specialist Tests + Non-Pharmac, then skim uplift and billing frequency conversion.')

      addParagraph('Claims Workflow:')
      addParagraph('Status progression: Pending → Adjudicate → Pre-Approved → Approved To Be Paid → Complete. Claims use integer IDs; is_paid flag and paid_date track payment status.')

      addParagraph('Authentication:')
      addParagraph('Supabase Auth with email/password. Protected routes use useAuth hook. Session persisted in localStorage.')

      addHeading('Troubleshooting', 2)
      addParagraph('Build Errors:')
      addCodeBlock(`# Clear node_modules and reinstall
rm -rf node_modules && npm install

# Clear Vite cache
rm -rf node_modules/.vite

# Check TypeScript errors
npx tsc --noEmit`)

      addParagraph('Database Connection Issues:')
      addBullet('Verify VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env file')
      addBullet('Check Supabase project status in dashboard')
      addBullet('Verify RLS policies are not blocking queries')

      addParagraph('Deployment Issues:')
      addBullet('Check GitHub Actions logs for build errors')
      addBullet('Verify VERCEL_TOKEN and Supabase env vars exist in GitHub Secrets')
      addBullet('Check Vercel build logs for failed steps')
      addBullet('Confirm vercel.json rewrites are present for SPA routing')

      addHeading('Performance Optimization', 2)
      addBullet('TanStack Query caching reduces database calls')
      addBullet('Lazy loading for route-based code splitting')
      addBullet('Vercel edge network for fast global delivery')
      addBullet('Optimized images and assets; tree-shaking removes unused code')

      addHeading('Useful Commands', 2)
      addParagraph('Development:')
      addCodeBlock(`npm run dev          # Start dev server
npm run build        # Build for production
npm run preview      # Preview production build
npm run lint         # Run ESLint
npm run type-check   # Check TypeScript types`)

      addParagraph('Deployment (manual):')
      addCodeBlock(`npm run build
vercel deploy --prebuilt --prod
# Or run vercel --prod to build and deploy in one step`)

      addHeading('Maintenance', 2)
      addBullet('Update dependencies: npm update (test thoroughly after)')
      addBullet('Database backups: Automatic daily via Supabase')
      addBullet('Monitor Supabase usage in dashboard')
      addBullet('Check GitHub Actions for failed builds')
      addBullet('Review Vercel usage monthly')

      addHeading('Support & Resources', 2)
      addBullet('React Docs: https://react.dev')
      addBullet('Supabase Docs: https://supabase.com/docs')
      addBullet('TanStack Query: https://tanstack.com/query')
      addBullet('Tailwind CSS: https://tailwindcss.com')
      addBullet('GitHub Actions: https://docs.github.com/actions')

      addParagraph('---')
      addParagraph('End of Technical Documentation')

      pdf.save('Rups_Groups_Documentation_Technical.pdf')
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Failed to generate PDF. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-nzrail-orange" />
            <h2 className="text-2xl font-semibold text-slate-900">System Documentation</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              className="btn-orange flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {isExporting ? 'Generating PDF...' : 'Export PDF'}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 px-6">
          <button
            onClick={() => setActiveTab('non-technical')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 flex items-center gap-2 ${
              activeTab === 'non-technical'
                ? 'border-nzrail-orange text-nzrail-orange'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <Users className="h-4 w-4" />
            Management Overview
          </button>
          <button
            onClick={() => setActiveTab('technical')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 flex items-center gap-2 ${
              activeTab === 'technical'
                ? 'border-nzrail-orange text-nzrail-orange'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <Code className="h-4 w-4" />
            Technical Documentation
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div id="system-docs-content" className="space-y-0">
            <div
              id="management-doc"
              className={activeTab === 'non-technical' ? '' : 'hidden'}
            >
              <NonTechnicalDocs />
            </div>
            <div
              id="technical-doc"
              className={activeTab === 'technical' ? '' : 'hidden'}
            >
              <TechnicalDocs />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function NonTechnicalDocs() {
  return (
    <div className="prose prose-slate max-w-none">
      <h1 className="text-3xl font-bold text-slate-900 mb-6">
        NZ Railways Staff Welfare Trust - System Overview
      </h1>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
        <p className="text-blue-900 font-medium mb-2">Document Purpose</p>
        <p className="text-blue-800 text-sm">
          This document provides a comprehensive overview of the NZ Railways Staff Welfare Trust medical insurance management system.
          It's designed to help management understand what the system does, how it works, and why certain technologies were chosen.
        </p>
      </div>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">What Is This System?</h2>
      <p className="text-slate-700 mb-4">
        The NZ Railways Staff Welfare Trust system is a modern web-based application that helps manage medical insurance for railway staff members.
        Think of it as a digital filing cabinet, calculator, and communication tool all rolled into one, accessible from any computer or device with an internet connection.
      </p>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Key Features</h3>
      <div className="space-y-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h4 className="font-semibold text-slate-900 mb-2">📋 Member Management</h4>
          <p className="text-slate-700 text-sm">
            View and manage all policy members, their details, coverage, and payment information in one central location.
            No more scattered spreadsheets or paper files.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h4 className="font-semibold text-slate-900 mb-2">💰 Premium Calculator</h4>
          <p className="text-slate-700 text-sm">
            Instantly calculate insurance premiums based on age, coverage options, and billing frequency.
            Compare multiple quote options side-by-side and generate professional PDFs for members.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h4 className="font-semibold text-slate-900 mb-2">📊 Rate Card Management</h4>
          <p className="text-slate-700 text-sm">
            Manage insurance pricing rates with different rate cards for different time periods.
            Upload new rates via CSV files and view them instantly across the system.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h4 className="font-semibold text-slate-900 mb-2">📑 Claims Processing</h4>
          <p className="text-slate-700 text-sm">
            Track and manage insurance claims with complete document storage, provider information, and payment history.
            All claim documents are securely stored and easily accessible.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h4 className="font-semibold text-slate-900 mb-2">📝 Audit Logging</h4>
          <p className="text-slate-700 text-sm">
            Every change made to member information is automatically tracked with who made the change, when, and what was changed.
            This provides complete accountability and helps resolve any disputes.
          </p>
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">How Does It Work?</h2>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">The Technology Stack (In Plain English)</h3>

      <div className="space-y-4 mb-6">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5">
          <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <span className="text-2xl">🌐</span> The Website (React)
          </h4>
          <p className="text-slate-700 text-sm mb-3">
            <strong>What it is:</strong> React is a modern technology for building websites. It's like LEGO blocks for web pages -
            you build small, reusable pieces that snap together to create the complete website.
          </p>
          <p className="text-slate-700 text-sm mb-3">
            <strong>Why we use it:</strong> React makes the website fast and responsive. When you click a button or update information,
            only the part of the page that needs to change will update, rather than reloading the entire page. This makes the system feel
            quick and modern, like a smartphone app rather than an old-fashioned website.
          </p>
          <p className="text-slate-700 text-sm">
            <strong>In practice:</strong> When you search for a member, type in a calculator field, or view a claim, React instantly
            shows you the results without any page refreshes or waiting.
          </p>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-5">
          <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
            <span className="text-2xl">🗄️</span> The Database (Supabase)
          </h4>
          <p className="text-slate-700 text-sm mb-3">
            <strong>What it is:</strong> Supabase is like a highly secure digital filing cabinet in the cloud. It stores all your member data,
            claims, rates, and documents in a structured, organized way.
          </p>
          <p className="text-slate-700 text-sm mb-3">
            <strong>Why we use it:</strong> Traditional databases require expensive servers and IT staff to maintain them. Supabase is a
            "cloud" service, which means someone else manages the servers and security for you. It's professionally monitored 24/7,
            automatically backed up, and scales as you grow.
          </p>
          <p className="text-slate-700 text-sm mb-3">
            <strong>Security:</strong> Supabase uses bank-level encryption. Your data is encrypted both when it's stored (at rest) and when
            it's being transmitted (in transit). Only authorized users with valid credentials can access the data.
          </p>
          <p className="text-slate-700 text-sm">
            <strong>In practice:</strong> When you look up a member, the website securely asks Supabase for that member's information.
            Supabase checks that you're authorized, then sends back only the data you're allowed to see.
          </p>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-5">
          <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
            <span className="text-2xl">📦</span> File Storage (Supabase Storage)
          </h4>
          <p className="text-slate-700 text-sm mb-3">
            <strong>What it is:</strong> This is where claim documents, invoices, and other files are stored - like a secure digital filing cabinet
            for documents rather than data.
          </p>
          <p className="text-slate-700 text-sm mb-3">
            <strong>Why we use it:</strong> Instead of storing files on someone's computer or a physical server, files are stored in the cloud
            with automatic backups. Files can't be lost if a computer breaks or is lost.
          </p>
          <p className="text-slate-700 text-sm">
            <strong>In practice:</strong> When you upload a claim document, it's securely stored and can be accessed by any authorized user
            from anywhere, at any time.
          </p>
        </div>

        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-5">
          <h4 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
            <span className="text-2xl">🚀</span> Website Hosting (Vercel Edge Network)
          </h4>
          <p className="text-slate-700 text-sm mb-3">
            <strong>What it is:</strong> Vercel hosts the website and API routes and serves them from a global edge network. The React app is built,
            cached, and delivered over HTTPS from the closest location to the user.
          </p>
          <p className="text-slate-700 text-sm mb-3">
            <strong>Why we use it:</strong> Vercel provides automatic builds, SSL, and a CDN-style edge network without extra setup. Copies of the app
            are stored around the world, so the site loads quickly whether you're in Auckland, Wellington, or overseas.
          </p>
          <p className="text-slate-700 text-sm">
            <strong>In practice:</strong> When code is merged, Vercel builds and deploys automatically. The new version is pushed to the edge network
            and served from the closest location to each user.
          </p>
        </div>

        <div className="bg-gradient-to-r from-slate-50 to-gray-50 border border-slate-300 rounded-lg p-5">
          <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <span className="text-2xl">🔧</span> Version Control (GitHub)
          </h4>
          <p className="text-slate-700 text-sm mb-3">
            <strong>What it is:</strong> GitHub is like a detailed history book for the website's code. Every change ever made is recorded with who made it,
            when, and why.
          </p>
          <p className="text-slate-700 text-sm mb-3">
            <strong>Why we use it:</strong> If something breaks, we can instantly go back to a previous version that worked. If we want to know why
            something works a certain way, we can look at the history. Multiple developers can work on the same project without interfering with each other.
          </p>
          <p className="text-slate-700 text-sm">
            <strong>In practice:</strong> Every time a developer makes an improvement or fixes a bug, they "commit" it to GitHub with a description.
            This creates a complete audit trail of every change to the system.
          </p>
        </div>

        <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-lg p-5">
          <h4 className="font-semibold text-cyan-900 mb-3 flex items-center gap-2">
            <span className="text-2xl">⚙️</span> Automated Deployment (GitHub Actions)
          </h4>
          <p className="text-slate-700 text-sm mb-3">
            <strong>What it is:</strong> GitHub Actions is an automation system that builds and deploys the website automatically whenever changes are made.
          </p>
          <p className="text-slate-700 text-sm mb-3">
            <strong>Why we use it:</strong> Instead of manually copying files to a server (which is error-prone and time-consuming), GitHub Actions does
            it automatically. Every time code is pushed to GitHub, it automatically builds the website, runs tests, and deploys it to Vercel - all in about 2-3 minutes.
          </p>
          <p className="text-slate-700 text-sm">
            <strong>In practice:</strong> When a developer fixes a bug or adds a feature, they push the code to GitHub. Within minutes, the live website
            is automatically updated with the changes - no manual intervention needed.
          </p>
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Data Flow: How Everything Connects</h2>
      <p className="text-slate-700 mb-4">
        Here's a simple explanation of what happens when you use the system:
      </p>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
        <ol className="space-y-4 text-slate-700">
          <li className="flex gap-3">
            <span className="font-bold text-nzrail-orange flex-shrink-0">1.</span>
            <div>
              <strong>You open the website:</strong> Vercel serves the website files from the nearest edge location to your browser in milliseconds.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="font-bold text-nzrail-orange flex-shrink-0">2.</span>
            <div>
              <strong>You log in:</strong> Supabase checks your username and password against its secure database. If correct, it gives your browser
              a special encrypted "token" that proves you're authorized.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="font-bold text-nzrail-orange flex-shrink-0">3.</span>
            <div>
              <strong>You search for a member:</strong> The React website sends a secure request to Supabase with your search terms and your token.
              Supabase checks the token is valid, searches its database, and sends back the results.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="font-bold text-nzrail-orange flex-shrink-0">4.</span>
            <div>
              <strong>You view results:</strong> React instantly displays the results without reloading the page. Everything feels fast and responsive.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="font-bold text-nzrail-orange flex-shrink-0">5.</span>
            <div>
              <strong>You upload a document:</strong> The file is uploaded directly to Supabase Storage with encryption. A reference to the file
              is stored in the database, linking it to the claim.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="font-bold text-nzrail-orange flex-shrink-0">6.</span>
            <div>
              <strong>You make a change:</strong> The change is sent to Supabase, which updates the database and automatically creates an audit log entry
              recording what changed, who changed it, and when.
            </div>
          </li>
        </ol>
      </div>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Security & Reliability</h2>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">How Your Data Is Protected</h3>
      <div className="space-y-3 mb-6">
        <div className="flex gap-3 items-start">
          <span className="text-green-600 font-bold">✓</span>
          <div className="text-slate-700 text-sm">
            <strong>Encryption:</strong> All data is encrypted using industry-standard AES-256 encryption - the same used by banks and government agencies.
          </div>
        </div>
        <div className="flex gap-3 items-start">
          <span className="text-green-600 font-bold">✓</span>
          <div className="text-slate-700 text-sm">
            <strong>Secure Authentication:</strong> Passwords are never stored in plain text. Even the database administrators cannot see user passwords.
          </div>
        </div>
        <div className="flex gap-3 items-start">
          <span className="text-green-600 font-bold">✓</span>
          <div className="text-slate-700 text-sm">
            <strong>Access Control:</strong> Users only see data they're authorized to access. The database enforces these rules automatically.
          </div>
        </div>
        <div className="flex gap-3 items-start">
          <span className="text-green-600 font-bold">✓</span>
          <div className="text-slate-700 text-sm">
            <strong>Automatic Backups:</strong> Supabase automatically backs up all data every day. Backups are kept for 7 days, allowing recovery from any accident or issue.
          </div>
        </div>
        <div className="flex gap-3 items-start">
          <span className="text-green-600 font-bold">✓</span>
          <div className="text-slate-700 text-sm">
            <strong>Audit Trail:</strong> Every data change is logged automatically, creating a complete audit trail for compliance and accountability.
          </div>
        </div>
        <div className="flex gap-3 items-start">
          <span className="text-green-600 font-bold">✓</span>
          <div className="text-slate-700 text-sm">
            <strong>Professional Monitoring:</strong> Vercel and Supabase have 24/7 security monitoring by dedicated teams watching for any threats or issues.
          </div>
        </div>
      </div>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Reliability & Uptime</h3>
      <p className="text-slate-700 mb-4">
        Both Vercel and Supabase target 99.9%+ uptime, meaning the system should only be unavailable for about 40 minutes per month maximum
        (usually much less). They have redundant systems in multiple regions, so if one fails, another automatically takes over.
      </p>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Costs & Scaling</h2>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Current Costs</h3>
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <p className="text-slate-700 text-sm mb-3">
          The system leverages free tiers extensively, keeping costs minimal with a "pay for what you use" model:
        </p>
        <ul className="space-y-2 text-slate-700 text-sm">
          <li><strong>Supabase:</strong> Free tier for development, approximately $25/month for production with current usage (database, authentication, file storage)</li>
          <li><strong>Vercel:</strong> Hobby tier covers current traffic; expect ~$0-20/month if traffic grows (hosting + edge delivery)</li>
          <li><strong>GitHub:</strong> Free for public repositories, Free Tier for private repositories (code repository and CI/CD)</li>
          <li><strong>Domain:</strong> ~$10-15/year</li>
          <li><strong>Total estimated monthly cost:</strong> $25-45/month (~$300-540/year)</li>
        </ul>
      </div>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Comparing to Traditional Hosting</h3>
      <p className="text-slate-700 mb-4">
        A traditional server-based system would require:
      </p>
      <ul className="list-disc list-inside space-y-2 text-slate-700 text-sm mb-4">
        <li>Physical server or virtual private server: $100-500/month</li>
        <li>Database server: $50-200/month</li>
        <li>IT staff time for maintenance, updates, security patches: $500-2000/month</li>
        <li>Backup systems and disaster recovery: $50-200/month</li>
        <li>Total: $700-2900/month</li>
      </ul>
      <p className="text-slate-700 mb-6">
        The modern cloud-based approach saves approximately $1,700-3,800 annually while providing better reliability, security, and performance.
      </p>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Scaling</h3>
      <p className="text-slate-700 mb-4">
        One of the biggest advantages of this architecture is automatic scaling:
      </p>
      <ul className="list-disc list-inside space-y-2 text-slate-700 text-sm mb-6">
        <li>If you add more members or users, the system automatically handles the increased load</li>
        <li>If traffic spikes (e.g., open enrollment period), Vercel automatically handles more requests on the edge network</li>
        <li>Storage grows automatically as you add more documents and data</li>
        <li>You only pay for what you actually use</li>
      </ul>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Maintenance & Updates</h2>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Who Maintains What</h3>
      <div className="space-y-3 mb-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="font-semibold text-green-900 mb-2">Maintained by Service Providers (No work required from you):</p>
          <ul className="list-disc list-inside space-y-1 text-green-800 text-sm">
            <li>Server hardware and infrastructure (Vercel)</li>
            <li>Database software updates and security patches (Supabase)</li>
            <li>Data center security and monitoring (Vercel/Supabase)</li>
            <li>Backup systems (Supabase)</li>
            <li>Network security and DDoS protection (Vercel)</li>
          </ul>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="font-semibold text-blue-900 mb-2">Maintained by Your Development Team:</p>
          <ul className="list-disc list-inside space-y-1 text-blue-800 text-sm">
            <li>Website features and functionality</li>
            <li>Bug fixes and improvements</li>
            <li>Adding new features as requirements change</li>
            <li>Updating React and other libraries (recommended every 6-12 months)</li>
          </ul>
        </div>
      </div>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Update Process</h3>
      <p className="text-slate-700 mb-4">
        When updates are needed, the process is straightforward:
      </p>
      <ol className="space-y-2 text-slate-700 text-sm list-decimal list-inside mb-6">
        <li>Developer makes changes to the code locally and tests them</li>
        <li>Developer pushes the changes to GitHub</li>
        <li>GitHub Actions automatically tests the code to catch any errors</li>
        <li>If tests pass, the new version is automatically deployed to Vercel</li>
        <li>The live website is updated within 2-3 minutes</li>
      </ol>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Risk Assessment & Mitigation</h2>

      <div className="space-y-4 mb-6">
        <div className="border border-slate-200 rounded-lg p-4">
          <h4 className="font-semibold text-slate-900 mb-2">Risk: Service Provider Outage</h4>
          <p className="text-slate-700 text-sm mb-2">
            <strong>What if Vercel or Supabase goes down?</strong>
          </p>
          <p className="text-slate-700 text-sm mb-2">
            <strong>Likelihood:</strong> Very low. Both target 99.9% uptime (about 40 minutes downtime per month maximum).
          </p>
          <p className="text-slate-700 text-sm">
            <strong>Mitigation:</strong> Both services have redundant systems across multiple data centers. If one fails, another automatically takes over.
            Additionally, all data is backed up daily, so even in a catastrophic failure, data can be restored.
          </p>
        </div>

        <div className="border border-slate-200 rounded-lg p-4">
          <h4 className="font-semibold text-slate-900 mb-2">Risk: Security Breach</h4>
          <p className="text-slate-700 text-sm mb-2">
            <strong>What if someone hacks into the system?</strong>
          </p>
          <p className="text-slate-700 text-sm mb-2">
            <strong>Likelihood:</strong> Very low with current security measures.
          </p>
          <p className="text-slate-700 text-sm">
            <strong>Mitigation:</strong> Multiple layers of security (encryption, authentication, access control), professional monitoring,
            automatic security updates, and complete audit logs. Supabase and Vercel maintain enterprise-grade security programs (SOC 2 / ISO 27001).
          </p>
        </div>

        <div className="border border-slate-200 rounded-lg p-4">
          <h4 className="font-semibold text-slate-900 mb-2">Risk: Developer Dependency</h4>
          <p className="text-slate-700 text-sm mb-2">
            <strong>What if the developer is unavailable?</strong>
          </p>
          <p className="text-slate-700 text-sm mb-2">
            <strong>Likelihood:</strong> Could happen (illness, leaving, etc.).
          </p>
          <p className="text-slate-700 text-sm">
            <strong>Mitigation:</strong> Complete source code is in GitHub with detailed history. Comprehensive documentation (like this).
            React is a very popular technology with thousands of developers available. System is designed to be maintainable by any
            competent React developer.
          </p>
        </div>

        <div className="border border-slate-200 rounded-lg p-4">
          <h4 className="font-semibold text-slate-900 mb-2">Risk: Data Loss</h4>
          <p className="text-slate-700 text-sm mb-2">
            <strong>What if data is accidentally deleted or corrupted?</strong>
          </p>
          <p className="text-slate-700 text-sm mb-2">
            <strong>Likelihood:</strong> Very low with current backup systems.
          </p>
          <p className="text-slate-700 text-sm">
            <strong>Mitigation:</strong> Automatic daily backups retained for 7 days. Complete audit log shows all changes.
            Database has "soft delete" features where data is marked as deleted but not actually removed, allowing easy recovery.
          </p>
        </div>

        <div className="border border-slate-200 rounded-lg p-4">
          <h4 className="font-semibold text-slate-900 mb-2">Risk: Cost Escalation</h4>
          <p className="text-slate-700 text-sm mb-2">
            <strong>What if costs suddenly increase?</strong>
          </p>
          <p className="text-slate-700 text-sm mb-2">
            <strong>Likelihood:</strong> Low with current usage patterns.
          </p>
          <p className="text-slate-700 text-sm">
            <strong>Mitigation:</strong> All services have usage alerts that can email if costs exceed thresholds. Current usage is well within
            free/low-cost tiers. Significant cost increase would require 10-100x increase in usage, which would be gradual and noticeable.
          </p>
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Future Considerations</h2>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Potential Enhancements</h3>
      <ul className="list-disc list-inside space-y-2 text-slate-700 text-sm mb-6">
        <li>Member portal for self-service (view coverage, submit claims, update details)</li>
        <li>Automated email notifications for claims status, payment reminders, etc.</li>
        <li>Mobile app for iOS and Android</li>
        <li>Integration with accounting systems for automated payment processing</li>
        <li>Advanced reporting and analytics dashboards</li>
        <li>Automated claim adjudication for simple claims</li>
      </ul>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Technology Evolution</h3>
      <p className="text-slate-700 mb-4">
        The technologies used (React, Supabase, Vercel) are all modern, well-supported, and designed for long-term use:
      </p>
      <ul className="list-disc list-inside space-y-2 text-slate-700 text-sm mb-6">
        <li>React is maintained by Meta (Facebook) and is the most popular web framework with millions of users</li>
        <li>Supabase is backed by major investors and has a large, growing community</li>
        <li>Vercel powers many production React deployments and is built to stay aligned with the ecosystem</li>
        <li>All technologies are open-source or have strong guarantees of long-term support</li>
      </ul>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Conclusion</h2>
      <p className="text-slate-700 mb-4">
        The NZ Railways Staff Welfare Trust system uses modern, proven technologies that provide excellent reliability, security, and
        value for money. While the technical details may seem complex, the end result is a system that:
      </p>
      <ul className="list-disc list-inside space-y-2 text-slate-700 mb-6">
        <li>Works reliably and quickly</li>
        <li>Keeps data secure and backed up</li>
        <li>Costs significantly less than traditional alternatives</li>
        <li>Can scale effortlessly as needs grow</li>
        <li>Can be maintained by any competent React developer</li>
        <li>Uses industry-standard technologies with long-term support</li>
      </ul>
      <p className="text-slate-700">
        The complexity is hidden "under the hood" - users simply see a fast, modern website that makes their jobs easier.
      </p>
    </div>
  )
}

function TechnicalDocs() {
  return (
    <div className="prose prose-slate max-w-none">
      <h1 className="text-3xl font-bold text-slate-900 mb-6">
        Technical Documentation
      </h1>

      <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6">
        <p className="text-orange-900 font-medium mb-2">For Developers</p>
        <p className="text-orange-800 text-sm">
          This section provides comprehensive technical documentation for developers who will be maintaining,
          enhancing, or troubleshooting the system.
        </p>
      </div>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Architecture Overview</h2>

      <div className="bg-slate-900 text-green-400 p-6 rounded-lg font-mono text-sm mb-6 overflow-x-auto">
        <pre>{`┌─────────────────────────────────────────────────────────────┐
│                         User's Browser                       │
│                     (React 19.1.1 + Vite)                    │
└──────────────────────────┬──────────────────────────────────┘
                           │
                           │ HTTPS
                           ↓
                  ┌────────────────────┐
                  │ Vercel Edge Network│
                  │  (Static + API)    │
                  └─────────┬──────────┘
                            │
                            ↓
                  ┌────────────────────┐
                  │ React Build + API  │
                  │ Routes (cached)    │
                  └────────────────────┘


┌───────────────────────────────────────────────────────────────┐
│                     Data Layer (Supabase)                     │
├───────────────────────────────────────────────────────────────┤
│                                                               │
│  ┌──────────────────┐         ┌────────────────────┐        │
│  │   PostgreSQL     │         │  Supabase Storage  │        │
│  │   Database       │         │  (File Storage)    │        │
│  └──────────────────┘         └────────────────────┘        │
│                                                               │
│  ┌──────────────────┐         ┌────────────────────┐        │
│  │  Authentication  │         │    Row Level       │        │
│  │  (Supabase Auth) │         │    Security        │        │
│  └──────────────────┘         └────────────────────┘        │
│                                                               │
└───────────────────────────────────────────────────────────────┘


┌───────────────────────────────────────────────────────────────┐
│                  Development & Deployment                     │
├───────────────────────────────────────────────────────────────┤
│                                                               │
│  Developer → GitHub → GitHub Actions → Vercel                │
│                                                               │
│  Auto-deploy on push to main branch                          │
│  Build, test, and deploy in ~2-3 minutes                     │
│                                                               │
└───────────────────────────────────────────────────────────────┘`}</pre>
      </div>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Technology Stack</h2>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Frontend</h3>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-4">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Technology</th>
              <th className="px-4 py-3 text-left font-semibold">Version</th>
              <th className="px-4 py-3 text-left font-semibold">Purpose</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            <tr>
              <td className="px-4 py-3 font-mono">React</td>
              <td className="px-4 py-3 font-mono">19.1.1</td>
              <td className="px-4 py-3">UI framework</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">TypeScript</td>
              <td className="px-4 py-3 font-mono">5.x</td>
              <td className="px-4 py-3">Type safety</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">Vite</td>
              <td className="px-4 py-3 font-mono">7.1.7</td>
              <td className="px-4 py-3">Build tool & dev server</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">React Router</td>
              <td className="px-4 py-3 font-mono">v7</td>
              <td className="px-4 py-3">Client-side routing</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">TanStack Query</td>
              <td className="px-4 py-3 font-mono">v5</td>
              <td className="px-4 py-3">Data fetching & caching</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">Tailwind CSS</td>
              <td className="px-4 py-3 font-mono">v3</td>
              <td className="px-4 py-3">Styling framework</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">Lucide React</td>
              <td className="px-4 py-3 font-mono">Latest</td>
              <td className="px-4 py-3">Icon library</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">html2canvas</td>
              <td className="px-4 py-3 font-mono">Latest</td>
              <td className="px-4 py-3">DOM to canvas for PDFs</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">jsPDF</td>
              <td className="px-4 py-3 font-mono">Latest</td>
              <td className="px-4 py-3">PDF generation</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Backend & Infrastructure</h3>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-4">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Service</th>
              <th className="px-4 py-3 text-left font-semibold">Provider</th>
              <th className="px-4 py-3 text-left font-semibold">Purpose</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            <tr>
              <td className="px-4 py-3 font-mono">PostgreSQL Database</td>
              <td className="px-4 py-3">Supabase</td>
              <td className="px-4 py-3">Primary data storage</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">Authentication</td>
              <td className="px-4 py-3">Supabase Auth</td>
              <td className="px-4 py-3">User authentication & sessions</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">File Storage</td>
              <td className="px-4 py-3">Supabase Storage</td>
              <td className="px-4 py-3">Document & file storage</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">Static Hosting & CDN</td>
              <td className="px-4 py-3">Vercel</td>
              <td className="px-4 py-3">Global edge delivery for the React app</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">API Routes</td>
              <td className="px-4 py-3">Vercel Functions</td>
              <td className="px-4 py-3">Serverless endpoints in /api</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">Source Control</td>
              <td className="px-4 py-3">GitHub</td>
              <td className="px-4 py-3">Code versioning & collaboration</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">CI/CD</td>
              <td className="px-4 py-3">GitHub Actions</td>
              <td className="px-4 py-3">Automated deployment</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Project Structure</h2>

      <div className="bg-slate-900 text-green-400 p-6 rounded-lg font-mono text-xs mb-6 overflow-x-auto">
        <pre>{`nzrail-react/
├── src/
│   ├── features/              # Feature-based modules
│   │   └── medical/
│   │       ├── members/       # Member management
│   │       ├── calculator/    # Premium calculator
│   │       ├── rates/         # Rate card management
│   │       ├── claims/        # Claims processing
│   │       ├── logs/          # Audit logs
│   │       └── shared/        # Shared medical utilities
│   │
│   ├── shared/                # Shared across all features
│   │   ├── components/        # Reusable UI components
│   │   ├── hooks/             # Custom React hooks
│   │   ├── lib/               # Utility libraries
│   │   └── types/             # Shared TypeScript types
│   │
│   ├── App.tsx                # Root application component
│   ├── main.tsx               # Application entry point
│   └── index.css              # Global styles
│
├── public/                    # Static assets
├── dist/                      # Build output (generated)
├── .github/
│   └── workflows/
│       └── deploy.yml         # CI/CD pipeline configuration
│
├── package.json               # Dependencies & scripts
├── tsconfig.json              # TypeScript configuration
├── vite.config.ts             # Vite build configuration
├── tailwind.config.js         # Tailwind CSS configuration
└── postcss.config.js          # PostCSS configuration`}</pre>
      </div>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Environment Variables & Configuration</h2>

      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
        <p className="text-amber-900 font-medium mb-2">⚠️ Security Note</p>
        <p className="text-amber-800 text-sm">
          The following variables are required but Supabase URL and anon key are safe to expose on the client side.
          Never commit service role keys or private keys to the repository.
        </p>
      </div>

      <div className="bg-slate-900 text-green-400 p-6 rounded-lg font-mono text-sm mb-6 overflow-x-auto">
        <pre>{`# .env file (for development)

# Supabase Configuration
VITE_SUPABASE_URL=https://fzxgdawafatrfpgdftrg.supabase.co
VITE_SUPABASE_ANON_KEY=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...

# Note: These are safe to expose as they're client-side only
# The database itself has Row Level Security (RLS) policies
# that prevent unauthorized access regardless of these keys`}</pre>
      </div>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Vercel Configuration</h3>
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <p className="text-slate-700 text-sm mb-3"><strong>Project Settings:</strong></p>
        <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm mb-4">
          <li><strong>Build command:</strong> <code className="bg-slate-100 px-2 py-1 rounded">cd nzrail-react && npm install && npm run build</code></li>
          <li><strong>Output directory:</strong> <code className="bg-slate-100 px-2 py-1 rounded">nzrail-react/dist</code></li>
          <li><strong>Framework:</strong> <code className="bg-slate-100 px-2 py-1 rounded">null</code> (handled via Vite build)</li>
          <li><strong>Rewrites:</strong> All routes → <code className="bg-slate-100 px-2 py-1 rounded">/index.html</code>, /api/* preserved for serverless routes</li>
          <li><strong>CI:</strong> GitHub Actions workflow <code className="bg-slate-100 px-2 py-1 rounded">.github/workflows/deploy-app.yml</code> uses Vercel CLI</li>
        </ul>

        <p className="text-slate-700 text-sm mb-3"><strong>Environment Variables (Vercel):</strong></p>
        <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm">
          <li><code className="bg-slate-100 px-2 py-1 rounded">VITE_SUPABASE_URL</code> and <code className="bg-slate-100 px-2 py-1 rounded">VITE_SUPABASE_ANON_KEY</code> for the React client</li>
          <li><code className="bg-slate-100 px-2 py-1 rounded">PULSE_SUPABASE_URL</code> and <code className="bg-slate-100 px-2 py-1 rounded">PULSE_SUPABASE_KEY</code> for Vercel API routes</li>
          <li><code className="bg-slate-100 px-2 py-1 rounded">VERCEL_TOKEN</code> stored in GitHub Secrets for CI deployment</li>
        </ul>
      </div>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Database Schema</h2>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Core Tables</h3>

      <div className="space-y-4 mb-6">
        <details className="bg-white border border-slate-200 rounded-lg">
          <summary className="px-4 py-3 font-semibold text-slate-900 cursor-pointer hover:bg-slate-50">
            medical_memberlist
          </summary>
          <div className="px-4 py-3 border-t border-slate-200 font-mono text-xs overflow-x-auto">
            <pre className="text-slate-700">{`Columns:
- id (uuid, PK)
- policy_number (integer)
- first_name (text)
- last_name (text)
- date_of_birth (date)
- gender (text)
- product_option (uuid, FK → medical_product_options)
- billing_frequency (uuid, FK → medical_billing_frequency)
- dental_optical (boolean)
- spectests (boolean)
- non_pharmac_addon (boolean)
- non_pharmac_excess (uuid, FK → medical_non_pharmac_excess)
- bank_account_name (text)
- bank_account_number (text)
- created_at (timestamp)
- updated_at (timestamp)

Indexes:
- policy_number (for fast policy lookups)
- last_name (for name searches)

RLS Policies:
- Authenticated users can SELECT all rows
- Only service role can INSERT/UPDATE/DELETE`}</pre>
          </div>
        </details>

        <details className="bg-white border border-slate-200 rounded-lg">
          <summary className="px-4 py-3 font-semibold text-slate-900 cursor-pointer hover:bg-slate-50">
            medical_rate_cards
          </summary>
          <div className="px-4 py-3 border-t border-slate-200 font-mono text-xs overflow-x-auto">
            <pre className="text-slate-700">{`Columns:
- id (integer, PK, auto-increment)
- name (text)
- effective_from (date)
- effective_to (date, nullable)
- ima_skim (numeric, default 0)
- elab_skim (numeric, default 0)
- trust_skim (numeric, default 0)
- created_at (timestamp)

Indexes:
- effective_from (for date range queries)

RLS Policies:
- Authenticated users can SELECT all rows
- Only service role can INSERT/UPDATE/DELETE`}</pre>
          </div>
        </details>

        <details className="bg-white border border-slate-200 rounded-lg">
          <summary className="px-4 py-3 font-semibold text-slate-900 cursor-pointer hover:bg-slate-50">
            medical_rates_monthly
          </summary>
          <div className="px-4 py-3 border-t border-slate-200 font-mono text-xs overflow-x-auto">
            <pre className="text-slate-700">{`Columns:
- id (uuid, PK)
- dates_of_prices (integer, FK → medical_rate_cards)
- age (integer)
- excess_nil (numeric)
- excess_250 (numeric)
- excess_500 (numeric)
- excess_1000 (numeric)
- excess_1500 (numeric)
- excess_2000 (numeric)
- excess_4000 (numeric)
- excess_6000 (numeric)
- dent_optics (numeric)
- specialists (numeric)
- m_non_pharmac_20 (numeric)
- m_non_pharmac_50 (numeric)
- m_non_pharmac_100 (numeric)
- m_non_pharmac_200 (numeric)
- m_non_pharmac_300 (numeric)
- f_non_pharmac_20 (numeric)
- f_non_pharmac_50 (numeric)
- f_non_pharmac_100 (numeric)
- f_non_pharmac_200 (numeric)
- f_non_pharmac_300 (numeric)

Indexes:
- (dates_of_prices, age) composite index

RLS Policies:
- Authenticated users can SELECT all rows
- Only service role can INSERT/UPDATE/DELETE`}</pre>
          </div>
        </details>

        <details className="bg-white border border-slate-200 rounded-lg">
          <summary className="px-4 py-3 font-semibold text-slate-900 cursor-pointer hover:bg-slate-50">
            medical_claims
          </summary>
          <div className="px-4 py-3 border-t border-slate-200 font-mono text-xs overflow-x-auto">
            <pre className="text-slate-700">{`Columns:
- id (uuid, PK)
- member_id (uuid, FK → medical_memberlist)
- claim_number (text, unique)
- service_date (date)
- received_date (date)
- status (text)
- provider_id (uuid, FK → medical_claim_providers)
- diagnosis (text)
- treatment_notes (text)
- benefit_max_id (uuid, FK → medical_claim_benefit_max)
- excess_amount (numeric)
- created_at (timestamp)
- updated_at (timestamp)

Indexes:
- member_id (for member claim lookups)
- claim_number (for quick claim lookup)
- status (for filtering by status)

RLS Policies:
- Authenticated users can SELECT all rows
- Only service role can INSERT/UPDATE/DELETE`}</pre>
          </div>
        </details>

        <details className="bg-white border border-slate-200 rounded-lg">
          <summary className="px-4 py-3 font-semibold text-slate-900 cursor-pointer hover:bg-slate-50">
            medical_member_changelog
          </summary>
          <div className="px-4 py-3 border-t border-slate-200 font-mono text-xs overflow-x-auto">
            <pre className="text-slate-700">{`Columns:
- id (uuid, PK)
- member_uuid (uuid, FK → medical_memberlist)
- user_id (uuid)
- field_name (text)
- old_value (text)
- new_value (text)
- change_type (text: 'UPDATE', 'INSERT', 'DELETE')
- changed_at (timestamp)
- notes (text, nullable)

Indexes:
- member_uuid (for member history)
- changed_at (for chronological queries)
- user_id (for user audit trail)

RLS Policies:
- Authenticated users can SELECT all rows
- Only triggers can INSERT
- No UPDATE/DELETE allowed (audit trail integrity)`}</pre>
          </div>
        </details>
      </div>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Lookup Tables</h3>
      <p className="text-slate-700 text-sm mb-4">
        All lookup tables use UUID primary keys for consistency:
      </p>
      <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm mb-6">
        <li><code className="bg-slate-100 px-2 py-1 rounded">medical_product_options</code> - Plan options (Excess levels)</li>
        <li><code className="bg-slate-100 px-2 py-1 rounded">medical_billing_frequency</code> - Billing frequencies (Weekly, Fortnightly, Monthly)</li>
        <li><code className="bg-slate-100 px-2 py-1 rounded">medical_non_pharmac_excess</code> - Non-Pharmac coverage levels</li>
        <li><code className="bg-slate-100 px-2 py-1 rounded">medical_claim_providers</code> - Healthcare providers</li>
        <li><code className="bg-slate-100 px-2 py-1 rounded">medical_claim_benefit_max</code> - Benefit maximums</li>
        <li><code className="bg-slate-100 px-2 py-1 rounded">medical_claim_notes</code> - Claim notes/comments</li>
      </ul>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Key Features Implementation</h2>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Premium Calculator</h3>
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <p className="text-slate-700 text-sm mb-3"><strong>Location:</strong> <code className="bg-slate-100 px-2 py-1 rounded">src/features/medical/calculator/</code></p>
        <p className="text-slate-700 text-sm mb-3"><strong>Key Components:</strong></p>
        <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm mb-3">
          <li><code>Calculator.tsx</code> - Main calculator interface</li>
          <li><code>utils/calculations.ts</code> - Pricing calculation logic</li>
          <li><code>hooks/useCalculatorData.ts</code> - Data fetching hooks</li>
          <li><code>types.ts</code> - TypeScript type definitions</li>
        </ul>
        <p className="text-slate-700 text-sm mb-3"><strong>Features:</strong></p>
        <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm">
          <li>Auto-selection of current rate card (or next rate card if within 2 months)</li>
          <li>Member search with auto-population of current policy details</li>
          <li>Multi-quote comparison (Option 1, Option 2, Option 3, etc.)</li>
          <li>Multi-member policy quotes (all members on same policy)</li>
          <li>PDF export of complete quote summary</li>
          <li>Real-time calculation with frequency conversion</li>
        </ul>
        <p className="text-slate-700 text-sm mb-3"><strong>Pricing Formula:</strong></p>
        <div className="bg-slate-900 text-green-400 p-4 rounded font-mono text-xs overflow-x-auto">
          <pre>{`// Age calculation uses current date (not rate card date)
const age = calculateAge(dob, new Date().toISOString())
const minAge = 20 // Minimum age for rates

// Base rate from rate card column (e.g., excess_500)
const baseNet = monthlyRate[rateCardColumn]

// Add add-ons
const dentalNet = hasDental ? monthlyRate.dent_optics : 0
const specNet = hasSpecialists ? monthlyRate.specialists : 0
const npNet = hasNonPharmac ? monthlyRate[npColumn] : 0

// Total net monthly
const netMonthly = baseNet + dentalNet + specNet + npNet

// Apply skim uplift
const skim = ima_skim + elab_skim + trust_skim
const grossMonthly = netMonthly / (1 - skim)

// Convert to billing frequency
if (frequency === 'weekly') {
  return grossMonthly * 12 / 52
} else if (frequency === 'fortnightly') {
  return grossMonthly * 12 / 26
} else {
  return grossMonthly // monthly
}`}</pre>
        </div>
      </div>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Claims Management</h3>
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <p className="text-slate-700 text-sm mb-3"><strong>Location:</strong> <code className="bg-slate-100 px-2 py-1 rounded">src/features/medical/claims/</code></p>
        <p className="text-slate-700 text-sm mb-3"><strong>Features:</strong></p>
        <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm mb-3">
          <li>Create, view, edit, delete claims</li>
          <li>Document upload to Supabase Storage</li>
          <li>Public URL generation for secure document access</li>
          <li>Provider management with autosave</li>
          <li>Claim notes with timestamps</li>
          <li>Payment history tracking</li>
          <li>Status workflow (Pending → Approved → Paid)</li>
        </ul>
        <p className="text-slate-700 text-sm mb-3"><strong>Storage Structure:</strong></p>
        <div className="bg-slate-900 text-green-400 p-4 rounded font-mono text-xs overflow-x-auto">
          <pre>{`Supabase Storage Bucket: medical-claims
├── public/ (publicly accessible with URL)
│   └── {claim-id}/
│       ├── invoice-1.pdf
│       ├── receipt-2.jpg
│       └── medical-report.pdf
│
Storage policies:
- Authenticated users can upload to public/
- Anyone with URL can read from public/
- Only service role can delete`}</pre>
        </div>
      </div>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Audit Logging</h3>
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <p className="text-slate-700 text-sm mb-3"><strong>Location:</strong> <code className="bg-slate-100 px-2 py-1 rounded">src/features/medical/logs/</code></p>
        <p className="text-slate-700 text-sm mb-3"><strong>Implementation:</strong></p>
        <p className="text-slate-700 text-sm mb-3">
          Audit logging is implemented using PostgreSQL triggers on the <code className="bg-slate-100 px-2 py-1 rounded">medical_memberlist</code> table.
          Every UPDATE, INSERT, or DELETE automatically creates an entry in <code className="bg-slate-100 px-2 py-1 rounded">medical_member_changelog</code>.
        </p>
        <div className="bg-slate-900 text-green-400 p-4 rounded font-mono text-xs overflow-x-auto">
          <pre>{`CREATE OR REPLACE FUNCTION log_member_changes()
RETURNS TRIGGER AS $$
BEGIN
  -- On UPDATE
  IF TG_OP = 'UPDATE' THEN
    -- Insert change log for each changed field
    -- (actual trigger is more complex, checking each field)
    INSERT INTO medical_member_changelog (
      member_uuid,
      user_id,
      field_name,
      old_value,
      new_value,
      change_type,
      changed_at
    ) VALUES (
      NEW.id,
      auth.uid(),
      'field_name',
      OLD.field_value::text,
      NEW.field_value::text,
      'UPDATE',
      NOW()
    );
  END IF;

  RETURN NEW;
END;
$$ LANGUAGE plpgsql SECURITY DEFINER;

CREATE TRIGGER member_changelog_trigger
AFTER INSERT OR UPDATE OR DELETE ON medical_memberlist
FOR EACH ROW EXECUTE FUNCTION log_member_changes();`}</pre>
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Development Workflow</h2>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Local Development Setup</h3>
      <div className="bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-sm mb-4 overflow-x-auto">
        <pre>{`# Clone repository
git clone https://github.com/Ntlope/rups_groups.git
cd rups_groups/nzrail-react

# Install dependencies
npm install

# Create .env file with Supabase credentials
echo "VITE_SUPABASE_URL=https://fzxgdawafatrfpgdftrg.supabase.co" > .env
echo "VITE_SUPABASE_ANON_KEY=your_anon_key_here" >> .env

# Start development server
npm run dev

# Development server starts at http://localhost:5173
# Hot module replacement (HMR) enabled
# Changes appear instantly without page refresh`}</pre>
      </div>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Build & Deploy Process</h3>
      <div className="bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-sm mb-4 overflow-x-auto">
        <pre>{`# Build for production
npm run build

# Output goes to dist/ directory
# Vite optimizes and minifies all assets
# Creates efficient code-split bundles

# Preview production build locally
npm run preview

# Deploy process (automated via GitHub Actions):
1. Developer pushes code to GitHub main branch
2. GitHub Actions workflow triggers (.github/workflows/deploy-app.yml)
3. Workflow checks out code
4. Installs Vercel CLI and pulls env vars
5. Builds project (vercel build --prod)
6. Deploys prebuilt output to Vercel (vercel deploy --prebuilt --prod)
7. Deployment complete (2-3 minutes total)`}</pre>
      </div>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Common Development Tasks</h3>

      <details className="bg-white border border-slate-200 rounded-lg mb-3">
        <summary className="px-4 py-3 font-semibold text-slate-900 cursor-pointer hover:bg-slate-50">
          Adding a New Feature Module
        </summary>
        <div className="px-4 py-3 border-t border-slate-200 text-sm">
          <div className="bg-slate-900 text-green-400 p-4 rounded font-mono text-xs overflow-x-auto">
            <pre>{`# 1. Create feature directory
mkdir -p src/features/medical/my-feature

# 2. Create component files
touch src/features/medical/my-feature/MyFeature.tsx
touch src/features/medical/my-feature/types.ts
touch src/features/medical/my-feature/hooks/useMyFeatureData.ts

# 3. Add route in App.tsx
// In src/App.tsx:
<Route path="/medical/my-feature" element={<MyFeature />} />

# 4. Add navigation link
// In appropriate navigation component:
<Link to="/medical/my-feature">My Feature</Link>`}</pre>
          </div>
        </div>
      </details>

      <details className="bg-white border border-slate-200 rounded-lg mb-3">
        <summary className="px-4 py-3 font-semibold text-slate-900 cursor-pointer hover:bg-slate-50">
          Adding a Database Table
        </summary>
        <div className="px-4 py-3 border-t border-slate-200 text-sm">
          <div className="bg-slate-900 text-green-400 p-4 rounded font-mono text-xs overflow-x-auto">
            <pre>{`# 1. Create table in Supabase SQL editor
CREATE TABLE my_table (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  name text NOT NULL,
  created_at timestamp DEFAULT now()
);

# 2. Add RLS policies
ALTER TABLE my_table ENABLE ROW LEVEL SECURITY;

CREATE POLICY "Authenticated users can select"
ON my_table FOR SELECT
TO authenticated
USING (true);

# 3. Create TypeScript types
// In src/features/medical/my-feature/types.ts:
export interface MyTable {
  id: string
  name: string
  created_at: string
}

# 4. Create data hook
// In src/features/medical/my-feature/hooks/useMyTableData.ts:
export function useMyTable() {
  return useQuery({
    queryKey: ['my-table'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('my_table')
        .select('*')

      if (error) throw error
      return data as MyTable[]
    }
  })
}`}</pre>
          </div>
        </div>
      </details>

      <details className="bg-white border border-slate-200 rounded-lg mb-3">
        <summary className="px-4 py-3 font-semibold text-slate-900 cursor-pointer hover:bg-slate-50">
          Debugging Production Issues
        </summary>
        <div className="px-4 py-3 border-t border-slate-200 text-sm">
          <p className="text-slate-700 mb-3"><strong>Browser Console Logs:</strong></p>
          <p className="text-slate-700 text-sm mb-3">
            Open browser DevTools (F12) and check Console tab for errors. Most errors will show here with stack traces.
          </p>

          <p className="text-slate-700 mb-3"><strong>Supabase Logs:</strong></p>
          <p className="text-slate-700 text-sm mb-3">
            Supabase Dashboard → Logs section shows database queries, errors, and slow queries.
          </p>

          <p className="text-slate-700 mb-3"><strong>Network Tab:</strong></p>
          <p className="text-slate-700 text-sm mb-3">
            Check DevTools Network tab to see failed API requests, slow queries, or 404s for missing assets.
          </p>

          <p className="text-slate-700 mb-3"><strong>Common Issues:</strong></p>
          <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm">
            <li>403 Forbidden: RLS policy denying access - check Supabase policies</li>
            <li>404 Not Found on page refresh: verify vercel.json rewrite to /index.html</li>
            <li>White screen: Check console for JavaScript errors</li>
            <li>Slow loading: Check Network tab waterfall for bottlenecks</li>
          </ul>
        </div>
      </details>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Security Best Practices</h2>

      <div className="space-y-3 mb-6">
        <div className="flex gap-3 items-start">
          <span className="text-green-600 font-bold">✓</span>
          <div className="text-slate-700 text-sm">
            <strong>Never commit secrets to Git:</strong> Use environment variables for all sensitive data. The <code className="bg-slate-100 px-2 py-1 rounded">.env</code> file
            is in <code className="bg-slate-100 px-2 py-1 rounded">.gitignore</code>.
          </div>
        </div>
        <div className="flex gap-3 items-start">
          <span className="text-green-600 font-bold">✓</span>
          <div className="text-slate-700 text-sm">
            <strong>Use RLS policies:</strong> Never disable Row Level Security. Always create policies that restrict access appropriately.
          </div>
        </div>
        <div className="flex gap-3 items-start">
          <span className="text-green-600 font-bold">✓</span>
          <div className="text-slate-700 text-sm">
            <strong>Validate user input:</strong> All form inputs should be validated both client-side (UX) and server-side (security).
          </div>
        </div>
        <div className="flex gap-3 items-start">
          <span className="text-green-600 font-bold">✓</span>
          <div className="text-slate-700 text-sm">
            <strong>Use parameterized queries:</strong> Supabase client library handles this automatically. Never concatenate user input into SQL strings.
          </div>
        </div>
        <div className="flex gap-3 items-start">
          <span className="text-green-600 font-bold">✓</span>
          <div className="text-slate-700 text-sm">
            <strong>Keep dependencies updated:</strong> Run <code className="bg-slate-100 px-2 py-1 rounded">npm audit</code> regularly and update vulnerable packages.
          </div>
        </div>
        <div className="flex gap-3 items-start">
          <span className="text-green-600 font-bold">✓</span>
          <div className="text-slate-700 text-sm">
            <strong>Use HTTPS everywhere:</strong> Vercel enforces HTTPS. Never make HTTP requests from the client.
          </div>
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Performance Optimization</h2>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Current Optimizations</h3>
      <ul className="list-disc list-inside space-y-2 text-slate-700 text-sm mb-6">
        <li><strong>Code Splitting:</strong> Vite automatically splits code into smaller chunks that load on demand</li>
        <li><strong>React Query Caching:</strong> API responses are cached in memory, reducing database queries</li>
        <li><strong>Vercel Edge Network:</strong> Static assets cached globally at edge locations</li>
        <li><strong>Lazy Loading:</strong> Images and large components load only when needed</li>
        <li><strong>Tree Shaking:</strong> Unused code is automatically removed during build</li>
        <li><strong>Asset Minification:</strong> JavaScript and CSS are minified and compressed</li>
      </ul>

      <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Performance Monitoring</h3>
      <div className="bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-xs mb-4 overflow-x-auto">
        <pre>{`# Lighthouse CI (built into Chrome DevTools)
# Scores:
# - Performance: 90+
# - Accessibility: 95+
# - Best Practices: 95+
# - SEO: 90+

# Bundle size analysis
npm run build
# Check dist/ directory sizes
# Main bundle should be < 500KB

# React Query DevTools (development only)
# Shows cache hits, query states, and refetch timings
# Accessible at bottom-left of screen in dev mode`}</pre>
      </div>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Troubleshooting Guide</h2>

      <details className="bg-white border border-slate-200 rounded-lg mb-3">
        <summary className="px-4 py-3 font-semibold text-slate-900 cursor-pointer hover:bg-slate-50">
          Build Failures
        </summary>
        <div className="px-4 py-3 border-t border-slate-200 text-sm">
          <p className="text-slate-700 mb-3"><strong>TypeScript Errors:</strong></p>
          <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm mb-3">
            <li>Run <code className="bg-slate-100 px-2 py-1 rounded">npm run type-check</code> to see all type errors</li>
            <li>Fix by adding proper type annotations or updating types</li>
            <li>Use <code className="bg-slate-100 px-2 py-1 rounded">// @ts-ignore</code> sparingly and only with comments explaining why</li>
          </ul>

          <p className="text-slate-700 mb-3"><strong>Dependency Issues:</strong></p>
          <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm mb-3">
            <li>Delete <code className="bg-slate-100 px-2 py-1 rounded">node_modules/</code> and <code className="bg-slate-100 px-2 py-1 rounded">package-lock.json</code></li>
            <li>Run <code className="bg-slate-100 px-2 py-1 rounded">npm install</code> to reinstall</li>
            <li>Check for conflicting peer dependencies</li>
          </ul>

          <p className="text-slate-700 mb-3"><strong>Vite Build Errors:</strong></p>
          <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm">
            <li>Clear Vite cache: <code className="bg-slate-100 px-2 py-1 rounded">rm -rf node_modules/.vite</code></li>
            <li>Check <code className="bg-slate-100 px-2 py-1 rounded">vite.config.ts</code> for configuration errors</li>
            <li>Ensure all imports are correct (case-sensitive on production)</li>
          </ul>
        </div>
      </details>

      <details className="bg-white border border-slate-200 rounded-lg mb-3">
        <summary className="px-4 py-3 font-semibold text-slate-900 cursor-pointer hover:bg-slate-50">
          Database Connection Issues
        </summary>
        <div className="px-4 py-3 border-t border-slate-200 text-sm">
          <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm">
            <li>Check <code className="bg-slate-100 px-2 py-1 rounded">VITE_SUPABASE_URL</code> is correct</li>
            <li>Verify <code className="bg-slate-100 px-2 py-1 rounded">VITE_SUPABASE_ANON_KEY</code> is valid</li>
            <li>Check Supabase project is not paused (free tier pauses after inactivity)</li>
            <li>Verify network connectivity (no firewall blocking Supabase)</li>
            <li>Check Supabase dashboard for service status</li>
          </ul>
        </div>
      </details>

      <details className="bg-white border border-slate-200 rounded-lg mb-3">
        <summary className="px-4 py-3 font-semibold text-slate-900 cursor-pointer hover:bg-slate-50">
          Deployment Issues
        </summary>
        <div className="px-4 py-3 border-t border-slate-200 text-sm">
          <p className="text-slate-700 mb-3"><strong>GitHub Actions Failure:</strong></p>
          <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm mb-3">
            <li>Check GitHub Actions logs for specific error</li>
            <li>Verify <code className="bg-slate-100 px-2 py-1 rounded">VERCEL_TOKEN</code> and Supabase env vars are set correctly in GitHub Secrets</li>
            <li>Ensure <code className="bg-slate-100 px-2 py-1 rounded">vercel.json</code> is present (build/output paths)</li>
          </ul>

          <p className="text-slate-700 mb-3"><strong>Vercel Deployment Checks:</strong></p>
          <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm mb-3">
            <li>Open the latest Vercel deployment URL from the Actions log to confirm the build succeeded</li>
            <li>If routes 404, confirm the SPA rewrite exists: <code className="bg-slate-100 px-2 py-1 rounded">/(.*) → /index.html</code></li>
            <li>If API routes fail, ensure required env vars (e.g., <code className="bg-slate-100 px-2 py-1 rounded">PULSE_SUPABASE_URL</code>) are set in Vercel</li>
          </ul>

          <p className="text-slate-700 mb-3"><strong>404 Errors After Deployment:</strong></p>
          <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm">
            <li>Verify <code className="bg-slate-100 px-2 py-1 rounded">vercel.json</code> rewrite catches all paths</li>
            <li>Hard refresh the browser to clear cached assets</li>
            <li>Re-run the deploy to push a fresh build if needed</li>
          </ul>
        </div>
      </details>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Useful Commands Reference</h2>

      <div className="bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-xs mb-6 overflow-x-auto">
        <pre>{`# Development
npm run dev              # Start development server
npm run build            # Build for production
npm run preview          # Preview production build
npm run type-check       # Check TypeScript types
npm run lint             # Run ESLint

# Git
git status               # Show current changes
git add .                # Stage all changes
git commit -m "message"  # Commit with message
git push                 # Push to GitHub (triggers deploy)
git pull                 # Pull latest changes
git log --oneline -10    # Show last 10 commits

# Vercel (requires Vercel CLI installed)
vercel pull --yes --environment=production   # Sync env vars locally
vercel build --prod                          # Build using Vercel
vercel deploy --prebuilt --prod              # Deploy built artifacts

# Supabase (requires Supabase CLI)
supabase db diff         # Show database schema changes
supabase db reset        # Reset local database
supabase functions deploy # Deploy edge functions

# Dependencies
npm install package-name # Install new dependency
npm update              # Update all dependencies
npm audit               # Check for vulnerabilities
npm audit fix           # Fix vulnerabilities`}</pre>
      </div>

      <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">Additional Resources</h2>

      <div className="space-y-3 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h4 className="font-semibold text-slate-900 mb-2">Official Documentation</h4>
          <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm">
            <li><a href="https://react.dev" target="_blank" rel="noopener" className="text-blue-600 hover:underline">React Documentation</a></li>
            <li><a href="https://vitejs.dev" target="_blank" rel="noopener" className="text-blue-600 hover:underline">Vite Documentation</a></li>
            <li><a href="https://supabase.com/docs" target="_blank" rel="noopener" className="text-blue-600 hover:underline">Supabase Documentation</a></li>
            <li><a href="https://tanstack.com/query/latest" target="_blank" rel="noopener" className="text-blue-600 hover:underline">TanStack Query Documentation</a></li>
            <li><a href="https://tailwindcss.com/docs" target="_blank" rel="noopener" className="text-blue-600 hover:underline">Tailwind CSS Documentation</a></li>
          </ul>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h4 className="font-semibold text-slate-900 mb-2">Vercel Resources</h4>
          <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm">
            <li><a href="https://vercel.com/docs" target="_blank" rel="noopener" className="text-blue-600 hover:underline">Vercel Documentation</a></li>
            <li><a href="https://vercel.com/docs/cli" target="_blank" rel="noopener" className="text-blue-600 hover:underline">Vercel CLI</a></li>
            <li><a href="https://vercel.com/dashboard" target="_blank" rel="noopener" className="text-blue-600 hover:underline">Vercel Dashboard</a></li>
          </ul>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h4 className="font-semibold text-slate-900 mb-2">Repository</h4>
          <ul className="list-disc list-inside space-y-1 text-slate-700 text-sm">
            <li><a href="https://github.com/Ntlope/rups_groups" target="_blank" rel="noopener" className="text-blue-600 hover:underline">GitHub Repository</a></li>
            <li><a href="https://github.com/Ntlope/rups_groups/actions" target="_blank" rel="noopener" className="text-blue-600 hover:underline">GitHub Actions (CI/CD)</a></li>
          </ul>
        </div>
      </div>

      <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
        <p className="text-green-900 font-medium mb-2">👍 Best Practice</p>
        <p className="text-green-800 text-sm">
          Always test changes locally before pushing to production. Use feature branches for significant changes,
          and create pull requests for code review before merging to main.
        </p>
      </div>

      <p className="text-slate-700 text-sm italic">
        This documentation is current as of the latest deployment. For questions or clarifications,
        check the GitHub repository README or contact the development team.
      </p>
    </div>
  )
}
